import React, { useState } from 'react';
import {
  CompactTable,
  Flex,
  Page,
  PageSidebar,
  PermissionGuard,
  useFilters,
  usePageQuery,
  User,
  defaultResponseHandler,
  formatMs,
  executeDownload,
} from '@mss/mss-component-library';
import Button from '../../../../common/Button';
import Breadcrumbs from '../../../../common/Breadcrumbs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisV, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useHistory, useRouteMatch } from 'react-router-dom';
import columns from './columns';
import AddUserModal from '../Add/Modal';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { Dropdown, Menu } from 'antd';
import { useQuery } from '@tanstack/react-query';
import Fetch from '../../../../common/util/_fetch';
import ImportModal from '../Add/ImportModal';
import dateFormat from 'dateformat';
import { sanitizedUrl } from '../../../../asset/util/sanitize';

const filterKeys: (keyof User)[] = [];
const searchKeys: (keyof User)[] = ['first_name', 'last_name', 'email'];

const customHeadingLabel = (t: TFunction) => (key: string) => {
  if (key === 'email') return t('Email');
  return key;
};

const PAGE_SIZE = 10;

export default function List() {
  const history = useHistory();
  const { url } = useRouteMatch();
  const [showAddUserModal, setShowModal] = useState(false);
  const [showImportUserModal, setShowImportModal] = useState(false);

  const { data: users, isFetching } = useQuery({
    queryKey: ['users'],
    initialData: [],
    refetchInterval: formatMs({ minutes: 5 }),
    queryFn: async () => {
      const { data, error } = await defaultResponseHandler<User[]>({
        t,
        request: Fetch.get('/api/user/'),
      });
      if (error) return [];
      return data.map(x => ({
        ...x,
        last_login_sortfix:
          x.last_login === null ? '1970-01-01T00:00:00.000Z' : x.last_login, // Never login converted to 0 Epoch time
      }));
    },
  });

  const [t] = useTranslation(['authorization', 'asset', 'common']);
  const [availableFilters, toggleFilter, filteredData, search] = useFilters(
    users,
    filterKeys,
    searchKeys,
    undefined,
    customHeadingLabel(t)
  );
  const [page, goToPage] = usePageQuery(filteredData.length / PAGE_SIZE);

  return (
    <PermissionGuard
      codename={['add_user', 'view_user', 'change_user', 'delete_user']}
    >
      <Page
        breadCrumbs={<Breadcrumbs />}
        padding={false}
        actions={
          <PermissionGuard codename="add_user">
            <Flex h="end" v="center">
              <Button
                onClick={() => {
                  if (!showAddUserModal) setShowModal(true);
                }}
                data-ta="newAssetButton"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-1" />{' '}
                {t('ADD USER')}
              </Button>
              <Dropdown
                overlay={
                  <Menu>
                    <Menu.Item
                      key="import"
                      className="text-left px-6 py-2"
                      onClick={() => setShowImportModal(true)}
                    >
                      {t('Import users')}
                    </Menu.Item>
                    <Menu.Item
                      className="text-left px-6 py-2"
                      key="export"
                      disabled={!users.length}
                      onClick={async () => {
                        const response = await Fetch.get(
                          `/api/user/csv_download/`
                        );
                        const data = await response.blob();
                        const url = window.URL.createObjectURL(
                          new Blob([data], { type: 'text/csv' })
                        );
                        executeDownload(
                          url,
                          `mss_users_${dateFormat(
                            new Date(),
                            'yyyy-mm-dd'
                          )}.csv`
                        );
                      }}
                    >
                      {t('Export users to CSV')}
                    </Menu.Item>
                  </Menu>
                }
                trigger={['click']}
                placement="bottomRight"
              >
                <Flex h="center" v="center" className="w-8 h-8 border-0">
                  <FontAwesomeIcon icon={faEllipsisV} />
                </Flex>
              </Dropdown>
            </Flex>
          </PermissionGuard>
        }
        main={
          <>
            <PermissionGuard codename="view_user">
              <CompactTable
                dataSource={filteredData}
                loading={isFetching}
                columns={columns(t)}
                rowKey={'id'}
                bordered={false}
                pagination={{
                  current: page,
                  showQuickJumper: true,
                  showSizeChanger: true,
                  total: filteredData.length,
                  showTotal: (total: number, range: any[]) =>
                    t('(RANGE1)-(RANGE2) of (TOTAL) items', {
                      range1: range[0],
                      range2: range[1],
                      TOTAL: total,
                    }),
                }}
                onChange={(pagination: any) => {
                  if (page !== pagination.current) goToPage(pagination.current);
                }}
                onRow={(record: any) => {
                  return {
                    onClick: () =>
                      history.push(
                        `${sanitizedUrl(url)}/${record.id}/settings`
                      ),
                  };
                }}
              />
            </PermissionGuard>
            {showAddUserModal && (
              <AddUserModal closeModal={() => setShowModal(false)} />
            )}
            {showImportUserModal && (
              <ImportModal closeModal={() => setShowImportModal(false)} />
            )}
          </>
        }
        sidebar={
          <PermissionGuard codename="view_user">
            <PageSidebar<User>
              availableFilters={availableFilters}
              toggleFilter={toggleFilter}
              search={search}
            />
          </PermissionGuard>
        }
      />
    </PermissionGuard>
  );
}
